//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import { SamplingCatalogProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiV2Model';
import { NO_PDP_URL } from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { validateNumber } from '@campaigns/utils/FormUtils';
import { isDefined, isEmptyValue } from '@utils/filterPredicates';

/**
 * Checks if a product has missing basic details.
 *
 * @param product - The product to check.
 * @returns True if any basic detail is missing, false otherwise.
 */
const hasMissingBasicDetails = (
  product: BVSamplingProduct | EditableBVSamplingProduct,
): boolean => {
  const requiredFields: (string | number | undefined)[] = [
    product.brand,
    product.category,
    product.description,
    product.imgUrl,
    product.name,
    product.targetCatalogProductId,
    product.quantity,
  ];

  return requiredFields.some(isEmptyValue);
};

/**
 * Checks if any variation of a product has missing details.
 *
 * @param product - The product to check.
 * @returns True if any variation detail is missing, false otherwise.
 */
const hasMissingVariationDetails = (
  product: BVSamplingProduct | EditableBVSamplingProduct,
): boolean => {
  if (!product.variations || product.variations.length === 0) {
    return true;
  }

  return product.variations.some((variation) => {
    const requiredFields: (string | number | undefined)[] = [
      variation.logisticsRef,
      variation.fairMarketValue,
      variation.quantity,
      variation.variantName,
      variation.imgUrl,
    ];

    return requiredFields.some(isEmptyValue);
  });
};

/**
 * Checks if the dimensions of a product are missing.
 *
 * @param product - The product to check.
 * @returns True if any dimension is missing, false otherwise.
 */
export const hasMissingDimensions = (product: BVSamplingProduct): boolean => {
  if (product.dimensions) {
    const { depth, format, height, width } = product.dimensions;
    return (
      isEmptyValue(format) ||
      isEmptyValue(width) ||
      isEmptyValue(height) ||
      isEmptyValue(depth)
    );
  }
  return true;
};

/**
 * Checks if a product has any missing details based on the box type.
 *
 * @param product - The product to check.
 * @returns True if any detail is missing, false otherwise.
 */
export const isMissingDetails = (
  product: BVSamplingProduct | EditableBVSamplingProduct | undefined,
): boolean => {
  if (!product) {
    return true;
  }

  return hasMissingBasicDetails(product) || hasMissingVariationDetails(product);
};

export const mapCatalogProductToBVSamplingProduct = (
  catalogProduct: SamplingCatalogProduct,
): BVSamplingProduct => {
  const transformedProduct: BVSamplingProduct = {
    brand: catalogProduct.brand,
    category: catalogProduct.category,
    description: catalogProduct.description,
    imgUrl: catalogProduct.imageUrl,
    name: catalogProduct.name,
    newProduct: false, // We need to set this to false to indicate that the product is not new and it's coming from the catalog
    pdpUrl:
      isEmptyValue(catalogProduct.productDetailPageUrl) ||
      catalogProduct.productDetailPageUrl === NO_PDP_URL
        ? undefined
        : catalogProduct.productDetailPageUrl,
    targetCatalogProductId: catalogProduct.id,
    variations: [
      {
        fairMarketValue: catalogProduct.fmv
          ? Number(catalogProduct.fmv)
          : undefined,
        quantity: undefined,
        variantName: undefined,
        logisticsRef: undefined,
        imgUrl: catalogProduct.imageUrl,
      },
    ],
  };

  const upcs = catalogProduct.upcs ?? [];
  const eans = catalogProduct.eans ?? [];
  const totalProductIdentifiers = upcs.length + eans.length;

  if (totalProductIdentifiers > 0 && transformedProduct.variations?.[0]) {
    if (totalProductIdentifiers === 1) {
      // If there's only one identifier, populate it to the GTIN/EAN/UPC field
      transformedProduct.variations[0].logisticsRef = upcs[0] ?? eans[0];
    } else {
      // If there are multiple identifiers, populate them to the GTIN/EAN/UPC for Syndication
      transformedProduct.destinationGTIN = upcs.concat(eans);
    }
  }

  return transformedProduct;
};

const transformDestinationGTIN = (
  destinationGTIN: string | undefined,
): string[] => {
  return destinationGTIN?.split(',').filter((gtin) => gtin.trim() !== '') ?? [];
};

export const prepareProductToSubmit = (
  data: EditableBVSamplingProduct,
): BVSamplingProduct => {
  const productToSubmit: BVSamplingProduct = {
    ...data,
    pdpUrl:
      isEmptyValue(data.pdpUrl) || data.pdpUrl === NO_PDP_URL
        ? undefined
        : data.pdpUrl,
    destinationGTIN: transformDestinationGTIN(data.destinationGTIN),
    quantity: toNumber(data.quantity),
    variations: data.variations.map((variation) => ({
      ...variation,
      fairMarketValue: toNumber(variation.fairMarketValue),
      quantity: toNumber(variation.quantity),
    })),
  };

  if (
    productToSubmit.variations?.[0] &&
    productToSubmit.variations.length === 1
  ) {
    const variation = productToSubmit.variations[0];
    variation.imgUrl = productToSubmit.imgUrl ?? variation.imgUrl;
    variation.variantName = productToSubmit.name ?? variation.variantName;
  }

  productToSubmit.quantity =
    productToSubmit.variations?.reduce(
      (acc, variation) => acc + (variation.quantity ?? 0),
      0,
    ) ?? 0;

  return productToSubmit;
};

const toNumber = (value: string | undefined): number | undefined => {
  if (!isDefined(value)) {
    return undefined;
  }
  const num = Number(value);
  return isNaN(num) ? undefined : num;
};

const convertToValidString = (
  value: number | undefined,
): string | undefined => {
  return isDefined(value) && validateNumber(value) ? String(value) : undefined;
};

export const getInitialProduct = (
  product: BVSamplingProduct | undefined,
): EditableBVSamplingProduct => {
  const initialProduct = {
    ...product,
    quantity: convertToValidString(product?.quantity),
    destinationGTIN: product?.destinationGTIN?.join(',') ?? '',
    variations: product?.variations?.map((variation) => ({
      ...variation,
      quantity: convertToValidString(variation.quantity),
      fairMarketValue: convertToValidString(variation.fairMarketValue),
    })) ?? [{ variantName: '' }],
    pdpUrl: product?.pdpUrl === NO_PDP_URL ? undefined : product?.pdpUrl,
  };

  return initialProduct;
};

export const mergeProductsInCampaignRequest = (
  existingProducts: BVSamplingProduct[] = [],
  newProducts: BVSamplingProduct[],
): BVSamplingProduct[] => {
  const existingProductMap = new Map(
    existingProducts.map((product) => [
      product.targetCatalogProductId,
      product,
    ]),
  );

  newProducts.forEach((newProduct) => {
    existingProductMap.set(newProduct.targetCatalogProductId, newProduct);
  });

  return Array.from(existingProductMap.values());
};
